import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { BellRing, CheckCircle2, CircleAlert } from 'lucide-react';
import { PUSH_STATUS_QUERY } from '../graphql/queries';
import {
  REGISTER_PUSH_SUBSCRIPTION_MUTATION,
  SEND_TEST_NOTIFICATION_MUTATION,
  UNREGISTER_PUSH_SUBSCRIPTION_MUTATION,
} from '../graphql/mutations';
import {
  getDeviceSubscriptionEndpoint,
  isPushSupported,
  subscribeDevice,
  unsubscribeDevice,
} from '../lib/push';
import { FOCUS_RING_CLASSES } from '../lib/focusTrap';

export interface PushStatus {
  available: boolean;
  enabled: boolean;
  vapidPublicKey: string | null;
}

interface PushStatusData {
  pushStatus: PushStatus;
}

/**
 * "Reminders" — the notifications card on Profile. Ask Alma "remind me
 * Thursday at 9 to call the dentist" and the agent files a dated task line in
 * reminders.md; this card is where a device opts in to receive the nudge when
 * a line comes due (Web Push — iPhone Home-Screen PWA, Android, desktop
 * alike). States: deployment has push off (or status won't load) → the card
 * hides; browser context can't hold a subscription (Safari tab on iOS) → the
 * install hint; otherwise turn on / turn off / send-a-test for THIS device.
 * Calm by design: only reminders the user asked for ever notify.
 */
export function RemindersCard() {
  const { data, loading, error, refetch } = useQuery<PushStatusData>(PUSH_STATUS_QUERY);
  // This browser's own subscription endpoint (null = none). Server `enabled`
  // says "some device is registered"; the buttons act on THIS device.
  const [deviceEndpoint, setDeviceEndpoint] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState<string | null>(null);

  const [registerSubscription] = useMutation(REGISTER_PUSH_SUBSCRIPTION_MUTATION);
  const [unregisterSubscription] = useMutation(UNREGISTER_PUSH_SUBSCRIPTION_MUTATION);
  const [sendTest, { loading: testing }] = useMutation<{ sendTestNotification: boolean }>(
    SEND_TEST_NOTIFICATION_MUTATION
  );

  const supported = isPushSupported();

  useEffect(() => {
    let cancelled = false;
    void getDeviceSubscriptionEndpoint().then((endpoint) => {
      if (!cancelled) setDeviceEndpoint(endpoint);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // The feature is off on this deployment, or the status can't load — either
  // way this stays a quiet non-surface rather than an error card.
  if (error) return null;
  if (data && !data.pushStatus.available) return null;

  const status = data?.pushStatus;

  const handleEnable = async () => {
    if (!status?.vapidPublicKey) return;
    setBusy(true);
    setErrorMessage(null);
    setStatusNote(null);
    try {
      const result = await subscribeDevice(status.vapidPublicKey);
      if (result.status === 'denied') {
        setErrorMessage(
          "Notifications are blocked for Alma in this browser's settings. Allow them there, then try again."
        );
        return;
      }
      if (result.status === 'unavailable') {
        setErrorMessage(
          'This browser could not set up notifications. On iPhone, open Alma from your Home Screen and try there.'
        );
        return;
      }
      await registerSubscription({ variables: result.subscription });
      setDeviceEndpoint(result.subscription.endpoint);
      setStatusNote('Notifications are on for this device.');
      await refetch().catch(() => {});
    } catch {
      setErrorMessage("That didn't save. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    setErrorMessage(null);
    setStatusNote(null);
    try {
      const endpoint = (await unsubscribeDevice()) ?? deviceEndpoint;
      if (endpoint) {
        await unregisterSubscription({ variables: { endpoint } }).catch(() => {});
      }
      setDeviceEndpoint(null);
      setStatusNote('Notifications are off for this device.');
      await refetch().catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setErrorMessage(null);
    setStatusNote(null);
    try {
      const result = await sendTest();
      setStatusNote(
        result.data?.sendTestNotification
          ? 'Test sent — check your notifications.'
          : 'Nothing was sent — no device is registered.'
      );
    } catch {
      setErrorMessage("The test didn't send. Try again in a moment.");
    }
  };

  return (
    <section
      aria-label="Reminders"
      className="glass-panel rounded-2xl p-5 sm:p-6 border border-[var(--card-border)] shadow-soft animate-fade-in-up"
    >
      <div className="flex items-center gap-3 mb-1">
        <BellRing className="w-5 h-5 text-[var(--text-secondary)] shrink-0" aria-hidden />
        <h2 className="text-lg font-semibold text-[var(--text-primary)]">Reminders</h2>
      </div>

      {loading && !data && <span className="skeleton block h-12 w-full rounded-xl" aria-hidden />}

      {status && (
        <>
          <p className="text-sm text-[var(--text-secondary)]">
            Ask me to remind you of something — &ldquo;remind me Thursday at 9 to call the
            dentist&rdquo; — and it lands in{' '}
            <Link
              to="/docs/reminders.md"
              className={`underline font-medium text-[var(--accent-ink)] ${FOCUS_RING_CLASSES}`}
            >
              your reminders document
            </Link>
            . Repeats work too — &ldquo;remind me every evening to fill in my journal.&rdquo;
            Notifications give this device a gentle nudge when one is due; check a reminder off and
            it stays quiet.
          </p>

          {!supported && (
            <p className="mt-3 text-sm text-[var(--text-tertiary)]">
              On iPhone, notifications need Alma installed to the Home Screen (Share → Add to Home
              Screen), then turned on from the installed app.
            </p>
          )}

          <div role="status" aria-live="polite">
            {statusNote && (
              <p className="mt-3 flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-600" aria-hidden />
                <span>{statusNote}</span>
              </p>
            )}
          </div>

          {errorMessage && (
            <p role="alert" className="mt-3 flex items-start gap-2 text-sm text-amber-700">
              <CircleAlert className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
              <span>{errorMessage}</span>
            </p>
          )}

          {supported && !deviceEndpoint && (
            <>
              {status.enabled && (
                <p className="mt-3 text-sm text-[var(--text-tertiary)]">
                  Notifications are already on for another device; this one can join too.
                </p>
              )}
              <button
                type="button"
                className="btn btn-primary mt-4"
                onClick={() => void handleEnable()}
                disabled={busy}
                aria-busy={busy}
              >
                {busy ? 'Turning on…' : 'Turn on notifications'}
              </button>
            </>
          )}

          {supported && deviceEndpoint && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => void handleTest()}
                disabled={testing}
                aria-busy={testing}
              >
                {testing ? 'Sending…' : 'Send a test'}
              </button>
              <button
                type="button"
                className="btn btn-ghost text-red-600"
                onClick={() => void handleDisable()}
                disabled={busy}
                aria-busy={busy}
              >
                {busy ? 'Turning off…' : 'Turn off on this device'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
